import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonStyles, DatePicker, Dropdown, Icons, Label, showAlert } from '@silverfort/sf-components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Collapse } from '@material-ui/core';
import ModulesField from './Fields/ModulesField';
import ReportBase from './ReportBase';
import { useReportsDrawer } from '../ReportsDrawerContext';
import { ReportType, RiskReportMetadata } from '../types';
import moment from 'moment-timezone';

const RiskReport: FC = () => {
    const tz = moment.tz.guess();
    const { generateReport, downloadReport, availableModules, riskDataStartDate } = useReportsDrawer();
    const [reportMetadata, setReportMetadata] = useState<RiskReportMetadata>({
        startDate: moment().tz(tz).startOf('day'),
        endDate: moment().tz(tz).endOf('day'),
        modules: []
    });
    const [isSingleDate, setIsSingleDate] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadableFileData, setDownloadableFileData] = useState<{ fileName: string; date: string } | null>(null);

    const minDate = useMemo(() => moment.utc(riskDataStartDate).tz(tz).startOf('day'), [riskDataStartDate, tz]);
    const maxDate = useMemo(() => moment.tz(tz).endOf('day'), [tz]);

    useEffect(() => {
        setReportMetadata((reportMetadata) => {
            return {
                ...reportMetadata,
                modules: availableModules.filter(({ disabled }) => !disabled).map(({ value }) => value) as string[]
            };
        });
    }, [availableModules]);

    const updateReportMetadata = <T extends keyof RiskReportMetadata>(key: T, value: RiskReportMetadata[T] | null) => {
        if (key === 'startDate' || key === 'endDate') {
            setReportMetadata((prevState) => ({ ...prevState, [key]: (value as moment.Moment)?.clone()?.tz(tz) }));
        } else setReportMetadata((prevState) => ({ ...prevState, [key]: value }));
    };

    const isValid = useMemo(() => {
        return (
            reportMetadata.modules.length &&
            reportMetadata.startDate?.isValid() &&
            reportMetadata.endDate?.isValid() &&
            reportMetadata.startDate.isSameOrBefore(reportMetadata.endDate) &&
            reportMetadata.startDate.isSameOrAfter(minDate) &&
            reportMetadata.endDate.isSameOrBefore(maxDate)
        );
    }, [maxDate, minDate, reportMetadata.endDate, reportMetadata.modules.length, reportMetadata.startDate]);

    const onGenerateReport = useCallback(async () => {
        setIsLoading(true);

        const metadata = { ...reportMetadata };

        if (isSingleDate) {
            metadata.startDate = metadata.startDate?.clone().startOf('day');
            metadata.endDate = metadata.startDate?.clone().endOf('day');
        } else {
            metadata.startDate = metadata.startDate?.clone().startOf('day');
            metadata.endDate = metadata.endDate?.clone().endOf('day');
        }

        const generatedReport = await generateReport({ type: ReportType.RiskReport, metadata });
        if (generatedReport && generatedReport.fileGenerated) {
            setDownloadableFileData(generatedReport);
        } else {
            setDownloadableFileData(null);
            showAlert('Error generating Risk Report', 'error');
        }
        setIsLoading(false);
    }, [generateReport, reportMetadata, isSingleDate]); // Report metadata is enough to recreate the function with the updated values

    const onDownloadReport = () => {
        if (downloadableFileData) {
            setIsLoading(true);
            const isDownloaded = downloadReport(downloadableFileData);
            if (isDownloaded) {
                setDownloadableFileData(null);
                showAlert('Risk Report downloaded successfully', 'success');
            } else {
                showAlert('Error downloading Risk Report', 'error');
            }
            setIsLoading(false);
        }
    };

    const footerContent = useMemo(
        () =>
            isLoading ? (
                <Button isLoading iconLeft={Icons.Loader} buttonStyle={ButtonStyles.Primary} />
            ) : downloadableFileData ? (
                <Button text="Download" onClick={onDownloadReport} buttonStyle={ButtonStyles.Primary} />
            ) : (
                <Button
                    text="Generate report"
                    buttonStyle={ButtonStyles.Primary}
                    disabled={!isValid}
                    onClick={onGenerateReport}
                />
            ),
        [isLoading, downloadableFileData, isValid, onGenerateReport]
    );
    return (
        <ReportBase
            headerTitle={'Generate a risk report'}
            headerDescription={'Generate and download a risk report (pptx)'}
            footerContent={footerContent}>
            <ModulesField
                modules={reportMetadata.modules}
                setModules={(modules) => updateReportMetadata('modules', modules)}
            />
            <div className="flex flex-col gap-2">
                <Label text={'Assessment period'} />
                <div className="flex flex-col p-4 border border-sf-gray-100 rounded-lg">
                    <Dropdown
                        variant={'trigger'}
                        items={[
                            { title: 'Single date', value: true },
                            { title: 'Date range', value: false }
                        ]}
                        selectedItems={[isSingleDate]}
                        onChange={(selected) => {
                            if (selected !== undefined) {
                                selected = Array.isArray(selected) ? selected[0] : selected;
                                setIsSingleDate(selected);
                            }
                        }}
                        size="large"
                        className="w-full"
                        fullMenuWidth
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            slotProps={{ popper: { sx: { zIndex: 5001 } } }}
                            value={reportMetadata.startDate}
                            onChange={(value) => updateReportMetadata('startDate', value)}
                            onAccept={(value) => updateReportMetadata('startDate', value)}
                            disableFuture={true}
                            maxDate={isSingleDate ? maxDate : reportMetadata.endDate}
                            minDate={minDate}
                            timezone={tz}
                            className="mt-4"
                        />
                        <Collapse in={!isSingleDate}>
                            <DatePicker
                                className="w-full mt-4"
                                slotProps={{ popper: { sx: { zIndex: 5001 } } }}
                                value={reportMetadata.endDate}
                                onChange={(value) => updateReportMetadata('endDate', value)}
                                onAccept={(value) => updateReportMetadata('endDate', value)}
                                disableFuture={true}
                                minDate={reportMetadata.startDate}
                                maxDate={maxDate}
                                timezone={tz}
                            />
                        </Collapse>
                    </LocalizationProvider>
                </div>
            </div>
        </ReportBase>
    );
};

export default RiskReport;
